import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useAppStore } from '@core/store'
import { formatarNumero } from '@viewer/report/documentModel'
import { viewerApi } from '@viewer/lib/viewerApi'
import KeyValueTable from '@viewer/inspector/KeyValueTable'
import { useUnidadeDe } from '@viewer/inspector/useUnidades'
import { canonicalIfcName as nomeDaClasse } from '@modules/boq/lib/ifcCanonical'
import { type GrupoDeType, filtrarTypes } from './agrupar'
import { type TypesDoModelo, lerTypes } from './lerTypes'

/** Todos os types do modelo, as suas instâncias, e os atributos e
 *  property sets de cada type. As propriedades da instância vêem-se no
 *  painel de propriedades, seleccionando-a aqui. */

function seleccionar(modelId: string, ids: number[]) {
  if (ids.length === 0) return
  const alvos = ids.map((localId) => ({ modelId, localId }))
  useAppStore.getState().applyBulkSelection(alvos, 'replace')
  void viewerApi.fitToItems(alvos)
}

export default function TypesBrowser() {
  const modelId = useAppStore((s) => s.activeModelId)
  const [carga, setCarga] = useState<{ modelId: string; dados: TypesDoModelo | null; erro: string | null } | null>(null)
  const [texto, setTexto] = useState('')
  const [abertas, setAbertas] = useState<Set<string>>(() => new Set())
  const unidadeDe = useUnidadeDe(modelId)

  useEffect(() => {
    if (!modelId) return
    let vivo = true
    lerTypes(modelId)
      .then((dados) => {
        if (vivo) setCarga({ modelId, dados, erro: null })
      })
      .catch((e) => {
        if (vivo) setCarga({ modelId, dados: null, erro: e instanceof Error ? e.message : String(e) })
      })
    return () => {
      vivo = false
    }
  }, [modelId])

  const dados = carga?.modelId === modelId ? carga.dados : null
  const classes = useMemo(() => (dados ? filtrarTypes(dados.classes, texto) : []), [dados, texto])
  const alternar = (chave: string) =>
    setAbertas((a) => {
      const n = new Set(a)
      if (n.has(chave)) n.delete(chave)
      else n.add(chave)
      return n
    })

  if (!modelId) {
    return <div className="p-3 text-[11px] text-[var(--color-text-muted)]">Abre um modelo para ver os seus types.</div>
  }
  if (carga?.modelId === modelId && carga.erro) {
    return <div className="p-3 text-[11px] text-[var(--color-error-text)]">Não foi possível ler os types: {carga.erro}</div>
  }
  if (!dados) {
    return <div className="p-3 text-[11px] text-[var(--color-text-muted)]">A ler os types…</div>
  }

  const repetidos = dados.classes.reduce((s, c) => s + c.grupos.filter((g) => g.repetido).length, 0)
  const semTypeTotal = dados.semType.reduce((s, c) => s + c.ids.length, 0)

  return (
    <div className="flex h-full min-h-0 flex-col text-[11px]">
      <div className="shrink-0 space-y-1.5 border-b border-[var(--color-border)] p-2">
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Procurar type, classe ou instância"
          className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-[11px] text-[var(--color-text)]"
        />
        <p className="text-[10px] text-[var(--color-text-muted)]">
          {formatarNumero(dados.totalTypes)} types em {formatarNumero(dados.classes.length)} classes.
          {repetidos > 0 && ` ${formatarNumero(repetidos)} nomes com vários types repetidos.`}
          {semTypeTotal > 0 && ` ${formatarNumero(semTypeTotal)} elementos sem type.`}
        </p>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-1">
        {dados.totalTypes === 0 && (
          <div className="m-1 rounded border border-[var(--color-warn-border)] bg-[var(--color-warn-bg)] p-2 text-[var(--color-warn-text)]">
            Este modelo não tem types (IfcXxxType). As propriedades vivem todas nas instâncias.
          </div>
        )}
        {classes.map((c) => {
          const aberta = abertas.has(c.categoria) || texto.trim() !== ''
          return (
            <div key={c.categoria} className="mb-0.5">
              <button
                type="button"
                onClick={() => alternar(c.categoria)}
                className="flex w-full items-center gap-1 rounded px-1 py-1 text-left hover:bg-[var(--color-surface)]"
              >
                {aberta ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <span className="font-medium text-[var(--color-text)]">{nomeDaClasse(c.categoria)}</span>
                <span className="ml-auto text-[10px] text-[var(--color-text-muted)]">
                  {formatarNumero(c.grupos.length)} · {formatarNumero(c.instancias)} inst.
                </span>
              </button>
              {aberta && (
                <div className="ml-3 border-l border-[var(--color-border)]/60 pl-1">
                  {c.grupos.map((g) => (
                    <Grupo
                      key={g.chave}
                      g={g}
                      aberto={abertas.has(g.chave)}
                      alternar={() => alternar(g.chave)}
                      modelId={modelId}
                      unidadeDe={unidadeDe}
                    />
                  ))}
                </div>
              )}
            </div>
          )
        })}
        {dados.semType.length > 0 && texto.trim() === '' && (
          <div className="mt-2 rounded border border-[var(--color-border)] p-1.5">
            <div className="mb-1 text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
              Sem type (instance-only)
            </div>
            {dados.semType.map((s) => (
              <button
                key={s.categoria}
                type="button"
                onClick={() => seleccionar(modelId, s.ids)}
                title="Seleccionar estes elementos"
                className="flex w-full items-center rounded px-1 py-0.5 text-left hover:bg-[var(--color-surface)]"
              >
                <span>{nomeDaClasse(s.categoria)}</span>
                <span className="ml-auto text-[var(--color-warn-text)]">{formatarNumero(s.ids.length)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function Grupo({
  g,
  aberto,
  alternar,
  modelId,
  unidadeDe,
}: {
  g: GrupoDeType
  aberto: boolean
  alternar: () => void
  modelId: string
  unidadeDe: ReturnType<typeof useUnidadeDe>
}) {
  const primeiro = g.types[0]
  return (
    <div className="mb-0.5">
      <button
        type="button"
        onClick={alternar}
        className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-left hover:bg-[var(--color-surface)]"
      >
        {aberto ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        <span className="min-w-0 flex-1 truncate text-[var(--color-text)]" title={g.nome}>
          {g.nome}
        </span>
        {g.repetido && (
          <span
            className="rounded border border-[var(--color-warn-border)] px-1 text-[9px] text-[var(--color-warn-text)]"
            title="Vários objectos type com o mesmo nome, em vez de um partilhado pelas instâncias. Acontece em exportações do Revit."
          >
            {formatarNumero(g.types.length)} types
          </span>
        )}
        <span className="text-[10px] text-[var(--color-text-muted)]">{formatarNumero(g.instancias.length)}</span>
      </button>
      {aberto && (
        <div className="mb-1 ml-3 space-y-1.5 rounded border border-[var(--color-border)]/60 bg-[var(--color-bg)]/40 p-1.5">
          {g.repetido && (
            <p className="text-[10px] text-[var(--color-warn-text)]">
              {formatarNumero(g.types.length)} types com este nome
              {g.psetsDiferem
                ? ', e com property sets diferentes entre si. Mostram-se os do primeiro.'
                : ', todos com os mesmos property sets: são cópias.'}
            </p>
          )}
          <div>
            <div className="mb-0.5 text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
              Atributos do type
            </div>
            <KeyValueTable rows={primeiro.atributos} emptyMessage="Sem atributos." />
          </div>
          <div>
            <div className="mb-0.5 text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
              Property sets do type ({formatarNumero(primeiro.psets.length)})
            </div>
            {primeiro.psets.length === 0 ? (
              <div className="text-[var(--color-text-muted)]">
                O type não tem property sets: as propriedades estão nas instâncias.
              </div>
            ) : (
              primeiro.psets.map((p, i) => (
                <div key={`${p.name}-${i}`} className="mb-1">
                  <div className="text-[10px] font-medium text-[var(--color-text)]">{p.name}</div>
                  <KeyValueTable rows={p.rows} unidadeDe={unidadeDe} emptyMessage="Sem propriedades." />
                </div>
              ))
            )}
          </div>
          <div>
            <div className="mb-0.5 flex items-center text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
              Instâncias ({formatarNumero(g.instancias.length)})
              {g.instancias.length > 1 && (
                <button
                  type="button"
                  onClick={() => seleccionar(modelId, g.instancias.map((i) => i.localId))}
                  className="ml-auto rounded border border-[var(--color-border)] px-1 normal-case tracking-normal hover:text-[var(--color-text)]"
                >
                  Seleccionar todas
                </button>
              )}
            </div>
            {g.instancias.length === 0 ? (
              <div className="text-[var(--color-text-muted)]">Nenhuma instância usa este type.</div>
            ) : (
              g.instancias.map((inst) => (
                <button
                  key={inst.localId}
                  type="button"
                  onClick={() => seleccionar(modelId, [inst.localId])}
                  title="Seleccionar: os atributos e as propriedades da instância aparecem no painel de propriedades."
                  className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-left hover:bg-[var(--color-surface)]"
                >
                  <span className="min-w-0 flex-1 truncate">{inst.nome || `#${inst.localId}`}</span>
                  <span className="text-[10px] text-[var(--color-text-dim)]">{nomeDaClasse(inst.categoria)}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
